import React, { useEffect, useRef } from "react";
import Placeholder from "../../../images/Place Holder 3 1.webp";

const buildRenderList = (chats) => {
  const ordered = [...chats].reverse();
  const result = [];
  let i = 0;

  const PREFERENCE_KEYWORDS = [
    "please select your prefrence",
    "please select your preference",
  ];

  const isPreferenceText = (chat) =>
    chat.message_type === "text" &&
    PREFERENCE_KEYWORDS.includes(chat.message?.trim().toLowerCase());

  while (i < ordered.length) {
    const current = ordered[i];

    if (
      isPreferenceText(current) &&
      i + 1 < ordered.length &&
      ordered[i + 1]?.message_type === "button"
    ) {
      const headerText = current.message;
      i++;

      // Collect all consecutive buttons
      const group = [];
      while (i < ordered.length && ordered[i].message_type === "button") {
        group.push(ordered[i]);
        i++;
      }
      result.push({ kind: "button_group", buttons: group, headerText });
    }
    // Standalone buttons not preceded by preference text
    else if (current.message_type === "button") {
      const group = [];
      while (i < ordered.length && ordered[i].message_type === "button") {
        group.push(ordered[i]);
        i++;
      }
      if (group.length === 1) {
        result.push({ kind: "single_button", chat: group[0] });
      } else {
        result.push({
          kind: "button_group",
          buttons: group,
          headerText: "Please Select Your Preference",
        });
      }
    } else {
      result.push({ kind: "single", chat: current });
      i++;
    }
  }

  return result;
};

const renderMarkdown = (text) => {
  if (!text) return null;
  return text.split("\n").map((line, li, arr) => {
    const parts = line.split(/(\*\*.*?\*\*)/g);
    return (
      <span key={li}>
        {parts.map((part, pi) =>
          part.startsWith("**") && part.endsWith("**") ? (
            <strong key={pi}>{part.slice(2, -2)}</strong>
          ) : (
            <span key={pi}>{part}</span>
          ),
        )}
        {li < arr.length - 1 && <br />}
      </span>
    );
  });
};

const ChatWindow = ({ chats, isLoadingChats, chatHasMore, fetchMoreChats }) => {
  const chatContainerRef = useRef(null);
  const prevHeight = useRef(0);
  const bottomRef = useRef(null);
  const loadingOld = useRef(false);

  const handleScroll = (e) => {
    const container = e.currentTarget;
    if (container.scrollTop < 120 && chatHasMore && !isLoadingChats) {
      prevHeight.current = container.scrollHeight;
      loadingOld.current = true;
      fetchMoreChats();
    }
  };

  useEffect(() => {
    const container = chatContainerRef.current;
    if (!container) return;
    if (loadingOld.current) {
      container.scrollTop = container.scrollHeight - prevHeight.current;
      loadingOld.current = false;
    } else {
      container.scrollTop = container.scrollHeight;
    }
  }, [chats]);

  const renderMessage = (chat) => {
    /* TEXT MESSAGE */
    if (chat.message_type === "text") {
      return (
        <p className="whitespace-pre-line text-left p-0 max-w-[500px] break-words m-0">
          {renderMarkdown(chat.message)}
        </p>
      );
    }

    /* IMAGE MESSAGE */
    if (chat.message_type === "image") {
      return (
        <div className="bg-[#E7FDD8] p-3 rounded-lg inline-block text-center">
          <img
            src={chat.message}
            alt="chat"
            className="w-full max-w-[350px] min-w-[250px] object-contain rounded bg-white block mx-auto"
            onError={(e) => {
              e.currentTarget.src = Placeholder;
            }}
          />
        </div>
      );
    }

    /* CATALOGUE MESSAGE */
    if (chat.message_type === "catalogue") {
      return (
        <div className="rounded-[10px] overflow-hidden border border-[#ddd] bg-[#D9FDD3] text-left p-[10px]">
          {/* Reply — catalogue */}
          {chat.reply_to && (
            <div className="border border-[#ccc] border-l-[3px] border-l-[#01C37D] rounded text-[13px] text-left bg-white text-[#555] mb-2 px-[10px] py-[6px]">
              {chat.reply_to}
            </div>
          )}

          {/* Header card: image + label */}
          <div className="flex">
            <img
              src="https://images.unsplash.com/photo-1550547660-d9450f859349"
              className="w-[150px] h-20 object-cover shrink-0"
              alt="catalogue"
              onError={(e) => {
                e.currentTarget.src = Placeholder;
              }}
            />

            <div className="flex items-center p-[10px] text-left bg-white text-black text-[13px]">
              <b>Your Catalogue on WhatsApp</b>
            </div>
          </div>

          <div className="p-[10px] text-[13px] text-left leading-[1.6] break-all whitespace-pre-wrap text-[#111] bg-[#D9FDD3]">
            {chat.message}
          </div>

          {/* Divider */}
          <div className="border-t border-[#eee]" />

          {/* View Catalogue button */}
          <div
            className="p-[10px] text-center text-[#00a884] font-semibold cursor-pointer bg-white text-[14px]"
            onClick={() => window.open(chat.message)}
          >
            View Catalogue
          </div>

          <div className="mt-[5px] text-right text-[14px] text-[#667781]">
            {chat.datetime}
          </div>
        </div>
      );
    }

    /* DOCUMENT MESSAGE */
    if (chat.message_type === "document") {
      return (
        <a href={chat.message} target="_blank" rel="noreferrer">
          📄 View Document
        </a>
      );
    }

    /* VIDEO MESSAGE */
    if (chat.message_type === "video") {
      return (
        <video controls src={chat.message} className="w-full rounded-lg" />
      );
    }

    /* TEMPLATE MESSAGE */
    if (chat.message_type === "template") {
      const header = chat.message?.components?.find((c) => c.type === "HEADER");
      const body = chat.message?.components?.find((c) => c.type === "BODY");
      const footer = chat.message?.components?.find((c) => c.type === "FOOTER");

      const headerImageUrl =
        header?.format === "IMAGE" && header?.example?.header_handle?.[0]
          ? header.example.header_handle[0]
          : header?.format === "IMAGE" && header?.parameters?.[0]?.image?.link
            ? header.parameters[0].image.link
            : null;

      return (
        <div className="max-w-[300px] p-[10px]">
          {header?.format === "IMAGE" && (
            <img
              src={headerImageUrl || Placeholder}
              alt="template"
              className="w-full h-[300px] object-cover rounded-md mb-3"
              onError={(e) => {
                e.currentTarget.src = Placeholder;
              }}
            />
          )}

          {body?.text && (
            <div className="text-[14px] whitespace-pre-wrap leading-[1.6] text-left">
              {renderMarkdown(body.text)}
            </div>
          )}

          {footer?.text && (
            <div className="text-[16px] mt-1.5 text-[#667781] text-left">
              {footer.text}
            </div>
          )}
        </div>
      );
    }
    return <p className="m-0">{chat.message}</p>;
  };

  const renderSingleButton = (chat) => {
    const isSent = chat.sender === "1";
    return (
      <div
        key={chat.message_id}
        className={`mb-[10px] ${isSent ? "text-right" : "text-left"}`}
      >
        <div
          className={`inline-block w-full max-w-[350px] min-w-[250px] rounded-[10px] overflow-hidden border border-[#e0e0e0] shadow-[0_1px_2px_rgba(0,0,0,0.08)] ${
            isSent ? "bg-white" : "bg-[#d9fdd3]"
          }`}
        >
          <div className="px-4 py-[11px] text-[14px] font-semibold text-[#00a884] text-center cursor-pointer">
            {chat.message}
          </div>
        </div>
      </div>
    );
  };

  const renderButtonGroup = (buttons, headerText, idx) => {
    const isSent = buttons[0]?.sender === "1";
    return (
      <div
        key={`btn_group_${idx}`}
        className={`mb-[10px] ${isSent ? "text-right" : "text-left"}`}
      >
        <div
          className={`inline-block w-full max-w-[350px] min-w-[250px] rounded-[10px] overflow-hidden border border-[#e0e0e0] shadow-[0_1px_2px_rgba(0,0,0,0.08)] ${
            isSent ? "bg-white" : "bg-[#d9fdd3]"
          }`}
        >
          {/* Header — from original text message */}
          <div className="px-[14px] pt-[10px] pb-[2px] text-[13px] font-medium text-[#111] text-left bg-[#d9fdd3]">
            {headerText}
          </div>

          {/* Timestamp below header */}
          <div className="text-[10px] text-[#667781] px-[14px] pb-[8px] text-right bg-[#d9fdd3]">
            {buttons[0]?.datetime}
          </div>

          {/* Each button with spacing */}
          <div className="flex flex-col gap-2 p-2">
            {buttons.map((btn, bIdx) => (
              <div
                key={btn.message_id || bIdx}
                className="rounded-lg border border-[#e0e0e0] px-4 py-[11px] text-center text-[14px] text-[#00a884] font-medium cursor-pointer bg-white transition-colors duration-150 hover:bg-[#f5f5f5]"
              >
                {btn.message}
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  };

  const renderList = buildRenderList(chats || []);

  return (
    <div
      ref={chatContainerRef}
      onScroll={handleScroll}
      className="hide-scrollbar h-full overflow-y-auto p-5"
    >
      {isLoadingChats && <div className="text-center py-2">Loading...</div>}

      {renderList.map((item, idx) => {
        /* ── BUTTON GROUP ── */
        if (item.kind === "button_group") {
          return renderButtonGroup(item.buttons, item.headerText, idx);
        }

        /* ── SINGLE BUTTON (Pay Now etc.) ── */
        if (item.kind === "single_button") {
          return renderSingleButton(item.chat);
        }

        /* ── NORMAL SINGLE MESSAGE ── */
        const chat = item.chat;
        const hideTimestamp = [
          "image",
          "video",
          "document",
          "template",
          "catalogue",
        ].includes(chat.message_type);

        return (
          <div
            key={chat.message_id}
            className={`mb-[10px] ${
              chat.sender === "1" ? "text-right" : "text-left"
            }`}
          >
            <div
              className={`inline-block max-w-[350px] rounded-[10px] ${
                ["image", "video", "catalogue"].includes(chat.message_type)
                  ? "p-0"
                  : "p-[10px]"
              } ${chat.sender === "1" ? "bg-[#D9FDD3]" : "bg-[#E7FDD8]"}`}
            >
              {/* REPLY MESSAGE */}
              {chat.reply_to && chat.message_type !== "catalogue" && (
                <div className="border border-[#ccc] border-l-[3px] border-l-[#01C37D] rounded text-[13px] text-left bg-white text-[#555] mb-[5px] px-[10px] py-[6px]">
                  {chat.reply_to}
                </div>
              )}

              {/* MESSAGE CONTENT */}
              {renderMessage(chat)}

              {/* TIMESTAMP */}
              {!hideTimestamp && (
                <div className="text-[10px] text-[#667781] mt-[5px] text-right">
                  {chat.datetime}
                </div>
              )}
            </div>
          </div>
        );
      })}

      <div ref={bottomRef} />
    </div>
  );
};

export default ChatWindow;
